import React, { useState } from "react";
import { reportContent, ReportTargetType } from "../api";

const REASONS = [
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "spam", label: "Spam or advertising" },
  { value: "scam", label: "Scam or fraud" },
  { value: "harassment", label: "Harassment or abuse" },
  { value: "fake", label: "Fake profile or review" },
  { value: "other", label: "Something else" },
];

interface Props {
  targetType: ReportTargetType;
  targetId: string;
  targetName?: string;
  onClose: () => void;
}

export default function ReportModal({ targetType, targetId, targetName, onClose }: Props) {
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    if (!reason) { setError("Please choose a reason"); return; }
    setSubmitting(true);
    setError("");
    try {
      await reportContent(targetType, targetId, reason, comment.trim() || undefined);
      setDone(true);
    } catch {
      setError("Could not submit the report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5" onClick={e => e.stopPropagation()}>
        {done ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-3">✅</div>
            <div className="font-bold text-navy mb-1">Report submitted</div>
            <p className="text-xs text-gray-500 mb-5">Thanks for helping keep Mouna safe. Our team will review it.</p>
            <button onClick={onClose} className="btn btn-primary btn-full">Done</button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-navy">Report {targetName || "content"}</h2>
              <button onClick={onClose} className="text-gray-400 text-2xl leading-none">×</button>
            </div>
            <div className="space-y-2 mb-4">
              {REASONS.map(r => (
                <label key={r.value} className={"flex items-center gap-3 rounded-xl px-4 py-3 cursor-pointer border transition-colors " +
                  (reason === r.value ? "border-mouna-blue bg-blue-pale" : "border-gray-200 hover:bg-gray-50")}>
                  <input type="radio" name="report-reason" value={r.value} checked={reason === r.value}
                    onChange={() => setReason(r.value)} className="accent-mouna-blue"/>
                  <span className="text-sm text-navy">{r.label}</span>
                </label>
              ))}
            </div>
            <textarea value={comment} onChange={e => setComment(e.target.value.slice(0, 1000))}
              placeholder="Add details (optional)" rows={3}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue mb-3 resize-none"/>
            {error && <div className="text-red-500 text-xs mb-3">{error}</div>}
            <button onClick={submit} disabled={submitting}
              className="btn btn-primary btn-full">
              {submitting ? "Submitting..." : "Submit Report"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
